import styled from 'styled-components';
import { ReactComponent as CopyIcon } from '@elementary/common-ui/assets/copy.svg';
import { ReactComponent as DownloadIcon } from '@elementary/common-ui/assets/download.svg';
import {
  AnomalyChart,
  AutoFreshnessChart,
  CodeEditor,
  Expandable,
  Flex,
  FlexCenter,
  FlexColumn,
  FreshnessChart,
  WidgetButton,
} from '@elementary/common-ui/components';
import SchemaChangesList from '@elementary/common-ui/components/SchemaChangesList';
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ExceptionCard from 'shared/components/ExceptionCard';
import SamplesTable from 'shared/components/SamplesTable';
import useSnackbar from 'shared/hooks/useSnackbar';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  TEST_OVERVIEW_SIDEPANEL_TAB,
  TEST_OVERVIEW_TAB_TEST_CONFIGURATION,
} from '@elementary/common-ui/constants/urlParams';
import {
  convertTableToCSV,
  downloadCSV,
  extractHeadersFromTableData,
} from 'shared/utils/csvExport';
import { SourceType } from 'shared/types';
import useTestOverviewExecution from 'shared/hooks/useTestOverviewExecution';
import {
  AnomalyChartTestExecutionData,
  AutoFreshnessTestExecutionData,
  CloudDataContractTestExecutionData,
  FreshnessChartExecutionData,
  SchemaChangesTestExecutionData,
  TableTestExecutionData,
  TestOverviewExecution,
} from 'shared/types/execution';
import ExecutionOverview from 'shared/components/ExecutionOverview';
import PanelError from 'shared/components/PanelError';
import CloudDataContractList from 'shared/components/CloudDataContractList';
import { removeBlankLines } from './utils';

const getTableData = (data: TestOverviewExecution | undefined) =>
  data?.data as TableTestExecutionData;

const getSchemaChangesData = (data: TestOverviewExecution | undefined) =>
  data?.data as SchemaChangesTestExecutionData;

const getAnomalyChartData = (data: TestOverviewExecution | undefined) =>
  data?.data as AnomalyChartTestExecutionData;

const getFreshnessChartData = (data: TestOverviewExecution | undefined) =>
  data?.data as FreshnessChartExecutionData;

const getAutoFreshnessData = (data: TestOverviewExecution | undefined) =>
  data?.data as AutoFreshnessTestExecutionData;

const getCloudDataContractData = (data: TestOverviewExecution | undefined) =>
  data?.data as CloudDataContractTestExecutionData;

const Description = styled(Typography)`
  font-weight: normal;
  color: ${({ theme }) => theme.palette.common.black};
  padding: 0px 8px;
  line-height: normal;
  white-space: pre-line;
`;

const WidgetButtonStyled = styled(WidgetButton)`
  background: ${({ theme }) => theme.palette.common.gray5};
  color: ${({ theme }) => theme.palette.common.black};
  border: 1px solid ${({ theme }) => theme.palette.common.gray};
  min-width: 0;
  height: 24px;
  border-radius: 8px;
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;

  :hover {
    border: 1px solid ${({ theme }) => theme.palette.common.gray};
    background: ${({ theme }) => theme.palette.common.gray14};
  }

  :disabled {
    background: ${({ theme }) => theme.palette.common.gray8};
    border: 1px solid ${({ theme }) => theme.palette.common.gray};
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ActionIconButton = styled(IconButton)`
  &.MuiIconButton-root {
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

type Props = {
  id: string;
  failures?: number | null;
  description?: string | null;
  onFeedbackClick?: () => void;
  isCloudTest?: boolean;
  sourceType: SourceType;
};

const TestOverviewExecutionDetails = ({
  id,
  failures,
  description,
  onFeedbackClick,
  isCloudTest,
  sourceType,
}: Props) => {
  const { openSnackbar } = useSnackbar();
  const [, setSearchParams] = useSearchParams();
  const warehouseType = useSelectedWarehouseType();

  const { data, error, isFetching } =
    useTestOverviewExecution<TestOverviewExecution>(id, sourceType);

  if (isFetching || error) {
    return (
      <FlexCenter sx={{ height: '100%', padding: '0 24px' }}>
        {error ? (
          <PanelError
            error={error as Error}
            errorName={'ApiError'}
            page={'TestOverviewExecutionDetails'}
          />
        ) : (
          <CircularProgress size={32} />
        )}
      </FlexCenter>
    );
  }

  const type = data?.data?.type;

  const handleExcludeRange = () => {
    setSearchParams((prev) => {
      prev.set(
        TEST_OVERVIEW_SIDEPANEL_TAB,
        TEST_OVERVIEW_TAB_TEST_CONFIGURATION,
      );
      return prev;
    });
  };

  const handleCopyTableAsCSV = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (type !== 'table') return;

    const tableData = getTableData(data);
    const tableRows = tableData?.data;

    if (!tableRows || tableRows.length === 0) {
      openSnackbar({ message: 'No data available to copy', variant: 'error' });
      return;
    }

    const headers = extractHeadersFromTableData(tableData, tableRows);

    const csvContent = convertTableToCSV(headers, tableRows);

    navigator.clipboard.writeText(csvContent);
    openSnackbar({
      message: 'Copied to clipboard',
      variant: 'success',
      duration: 1500,
    });
  };

  const handleDownloadTableAsCSV = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (type !== 'table') return;

    const tableData = getTableData(data);
    const tableRows = tableData?.data;

    if (!tableRows || tableRows.length === 0) {
      openSnackbar({
        message: 'No data available to download',
        variant: 'error',
      });
      return;
    }

    const headers = extractHeadersFromTableData(tableData, tableRows);

    const csvContent = convertTableToCSV(headers, tableRows);

    downloadCSV(csvContent, `result-samples-${id}.csv`);
  };

  const displayDescription = description ? removeBlankLines(description) : '';

  const shouldShowExpandable =
    !data?.exception && (displayDescription || data?.data);

  const trainingPeriod =
    type === 'auto_freshness'
      ? getAutoFreshnessData(data).trainingPeriod
      : type === 'anomaly_chart'
      ? getAnomalyChartData(data).trainingPeriod
      : null;

  const detectionPeriod =
    type === 'anomaly_chart' ? getAnomalyChartData(data).detectionPeriod : null;

  return (
    <>
      <ExecutionOverview
        data={data as TestOverviewExecution}
        trainingPeriod={trainingPeriod}
        detectionPeriod={detectionPeriod}
        onFeedbackClick={onFeedbackClick}
      />

      {data?.exception && <ExceptionCard exception={data?.exception} />}

      {shouldShowExpandable && (
        <Expandable
          title={
            <ActionButtonsContainer>
              <span>
                {failures
                  ? `Result sample${
                      type === 'table'
                        ? ` (${getTableData(data).data.length}/${failures})`
                        : ''
                    }`
                  : 'Result'}
              </span>
              {type === 'table' && (
                <Flex gap={1}>
                  <Tooltip title="Copy as CSV">
                    <ActionIconButton onClick={handleCopyTableAsCSV}>
                      <CopyIcon />
                    </ActionIconButton>
                  </Tooltip>
                  <Tooltip title="Download as CSV">
                    <ActionIconButton onClick={handleDownloadTableAsCSV}>
                      <DownloadIcon />
                    </ActionIconButton>
                  </Tooltip>
                </Flex>
              )}
            </ActionButtonsContainer>
          }
          defaultExpanded
        >
          {displayDescription && (
            <Description sx={{ mb: '16px' }}>{displayDescription}</Description>
          )}
          {data?.data && (
            <>
              {type === 'table' && (
                <SamplesTable
                  data={getTableData(data).data || []}
                  columns={getTableData(data).headers}
                />
              )}
              {type === 'schema_changes' && (
                <SchemaChangesList
                  displayName={getSchemaChangesData(data).displayName}
                  description={getSchemaChangesData(data).description}
                />
              )}
              {type === 'cloud_data_contract' &&
                getCloudDataContractData(data).changes.length > 0 && (
                  <CloudDataContractList
                    changes={getCloudDataContractData(data).changes}
                  />
                )}

              {type === 'anomaly_chart' && (
                <FlexColumn sx={{ position: 'relative' }}>
                  {isCloudTest && (
                    <Tooltip title="Exclude specific time range from the training period">
                      <WidgetButtonStyled onClick={handleExcludeRange}>
                        EXCLUDE
                      </WidgetButtonStyled>
                    </Tooltip>
                  )}

                  <AnomalyChart
                    metrics={getAnomalyChartData(data).metrics}
                    metricsTitle={getAnomalyChartData(data).metricsTitle}
                    failOnZero={getAnomalyChartData(data).failOnZero}
                    anomalyDirection={
                      getAnomalyChartData(data).anomalyDirection
                    }
                    detectionPeriod={getAnomalyChartData(data).detectionPeriod}
                    detectionDelay={getAnomalyChartData(data).detectionDelay}
                    status={data.status}
                  />
                </FlexColumn>
              )}

              {type === 'freshness_chart' && (
                <FreshnessChart
                  lastTimestamp={getFreshnessChartData(data).lastTimestamp}
                  status={data.status}
                  maxLoadedAtTimeAgoInSec={
                    getFreshnessChartData(data).maxLoadedAtTimeAgoInS
                  }
                  warnAfterRange={getFreshnessChartData(data).warnAfterRange}
                  errorAfterRange={getFreshnessChartData(data).errorAfterRange}
                  loadedAtField={getFreshnessChartData(data).loadedAtField}
                />
              )}
              {type === 'auto_freshness' && (
                <AutoFreshnessChart
                  metrics={getAutoFreshnessData(data).metrics}
                  status={data.status}
                />
              )}
            </>
          )}
        </Expandable>
      )}

      {data?.query && (
        <Expandable
          title="Query"
          defaultExpanded
          actionButtonTitle="Copy result query"
          actionButtonIcon={<CopyIcon width={16} />}
          onActionButtonClick={() => {
            navigator.clipboard.writeText(data.query);
            openSnackbar({ message: 'Copied to clipboard' });
          }}
        >
          <CodeEditor
            mode="sql"
            warehouseType={warehouseType}
            value={data.query}
            readOnly
          />
        </Expandable>
      )}
    </>
  );
};

export default TestOverviewExecutionDetails;
